/**
 * Arama reklamları hesabı bağlama sözleşmesi (v3.2 D — SALT OKUNUR). Belge: docs/spec/castinflow/google-ads-connect.md.
 * Karar: ADR-0016 § 1, § 3, § 8 ("Castinflow reklam hesabına yazmaz"). Sosyal medya reklam hesabı bağlamayla (CA-4;
 * `ad-account-connect.ts`) AYNI ürün deneyimi: yetkilendir → dönüş → hesap seç → bağla. Ortak parçalar (opak `state` / bilet
 * biçimi, yetkilendirme yanıtı, bağlama girdisi ve sonucu, dönüş sorgusunun sınıflandırılması) o dosyadan kullanılır.
 *
 * Bu sağlayıcıya özgü olanlar:
 * - Sağlayıcının SALT-OKUNUR yetki kapsamı YOKTUR (resmî belge: okuma ve yazma aynı kapsamı kullanır). İstenen tek kapsam
 *   `GOOGLE_ADS_SCOPE`'tur; salt-okunurluk UYGULAMADA zorlanır (yalnız okuma çağrıları; reklam okuma kapısı). Ekran, verilen
 *   iznin kapsamı ile Castinflow'un yaptığını ayrı ayrı söyler.
 * - YÖNETİCİ hesabı harcama kaynağı olarak BAĞLANMAZ: yönetici hesapları `managers` listesinde GRUP olarak gelir (seçilemez);
 *   altlarındaki reklam hesapları `accounts` içinde, hangi yönetici üzerinden erişildiği bilgisiyle listelenir. Doğrudan erişilen
 *   reklam hesabı da bağlanabilir (aynı hesaba iki yoldan erişiliyorsa doğrudan erişim seçilir).
 * - Hesap: maskeli kimlik, ad, para birimi (ISO 4217), saat dilimi (IANA; harcama günleri bu dilimdedir), durum, test hesabı mı.
 * - Kapalı durumlar ayrı ayrı bildirilir (`googleAdsConnectStatusSchema`): gerçek kip kapalı · erişim seviyesi yalnız test
 *   hesaplarına izin veriyor · uygulama doğrulaması bekliyor. Hiçbiri başarı vaat etmez.
 *
 * Hiçbir yanıt yetki kodu, belirteç ya da tam dış hesap kimliği taşımaz. Arayüz metinlerinde üçüncü taraf marka adı
 * kullanılmaz (genel kategori adları); sözleşme anahtarları kod tanımlayıcısıdır.
 */
import { z } from 'zod';
import { AD_CONNECT_ACCOUNT_KEY_RE, AD_CONNECT_OPAQUE_RE, adConnectAuthorizeResultSchema, adConnectConnectInputSchema, adConnectConnectResultSchema, adConnectExchangeInputSchema, } from './ad-account-connect.js';
import { GOOGLE_ADS_ACCESS_LEVELS } from './google-ads-status.js';
import { planQuotaStatusSchema } from './plans.js';
import { PROVIDER_CONNECTION_STATUSES, SIMULATOR_SCENARIOS } from './provider-connections.js';
// Erişim seviyesi, doğrulama durumu ve bölüm durumu ayrı dosyadadır (bağlantı listesi sözleşmesi de kullanır; döngüsüz içe aktarma)
export * from './google-ads-status.js';
// v3.3: merkezi API işlem bütçesi sözleşmesi (öncelik sınıfları, bekleme nedenleri, bölümdeki bütçe durumu)
export * from './google-ads-budget.js';
/**
 * Onay ekranından dönüş sayfası (uygulama içi rota; sağlayıcıya verilen yönlendirme adresi `<web kökü>` + bu yol — TAM
 * eşleşme). Sosyal medya dönüş yolundan AYRIDIR: sayfa hangi sağlayıcının değişim ucunu çağıracağını rotadan bilir.
 */
export const GOOGLE_ADS_CONNECT_CALLBACK_PATH = '/settings/connections/ads/google/callback';
/** İstenen TEK kapsam. Sağlayıcının salt-okunur kapsamı yoktur: bu kapsam teknik olarak yazmaya da izin verir. */
export const GOOGLE_ADS_SCOPE = 'https://www.googleapis.com/auth/adwords';
/**
 * Kullanıcının, sağlayıcı hesabında bu uygulamaya verdiği yetkiyi KENDİSİNİN kaldırdığı resmî hesap yönetimi sayfası (v3.3).
 * Castinflow bağlantı kesmede sağlayıcıdaki yetkiyi iptal ETMEZ (yetki kullanıcı + uygulama düzeyinde paylaşılır; belge § 9.3):
 * kesme onayı ve kesilmiş bağlantı kartı bu adresi yeni sekmede, `rel="noopener noreferrer"` ile gösterir. Adres ve adımlar
 * resmî hesap yardım belgesinden doğrulandı (support.google.com/accounts/answer/13533235; okuma: 12 Ekim 2026): bağlı
 * uygulamalar sayfası → uygulama → "Ayrıntıları göster" → "Erişimi kaldır" → "Onayla". Değer kod tanımlayıcısıdır; görünen
 * metinlerde üçüncü taraf marka adı kullanılmaz.
 */
export const GOOGLE_ADS_GRANT_MANAGEMENT_URL = 'https://myaccount.google.com/linkedapps';
export const GOOGLE_ADS_CONNECT_LIMITS = {
    /** `state` rastgele bayt sayısı (≥ 32) ve ömrü (saniye; süre esaslı) */
    stateBytes: 32,
    stateTtlSeconds: 600,
    /** Seçim bileti ömrü (saniye; süre esaslı) */
    ticketTtlSeconds: 600,
    /** Kod doğrulayıcı (PKCE) rastgele bayt sayısı (32 bayt → 43 karakter; sağlayıcı sınırı 43–128) */
    pkceVerifierBytes: 32,
    /** Tek istekte bağlanabilecek / yenilenebilecek hesap */
    maxSelect: 20,
    /** Girişin DOĞRUDAN eriştiği hesaplardan en çok kaçının ayrıntısı okunur (aşan kısım `truncated`) */
    maxAccessible: 20,
    /** Bir yönetici hesabının altından listelenen en çok hesap (aşan kısım o yöneticide `truncated`) */
    maxClientsPerManager: 200,
    /** Kullanıcı başına pencere içinde en çok `authorize` ve `exchange` */
    authorizePerWindow: 20,
    exchangePerWindow: 10,
    rateWindowSeconds: 600,
};
/** Reklam hesabının (müşteri) dış kimliği: tam 10 rakam (görünen biçim `123-456-7890`, tiresiz saklanır) */
export const GOOGLE_ADS_CUSTOMER_ID_RE = /^\d{10}$/;
/** Kod doğrulayıcı (PKCE): ayrılmamış karakterler, 43–128 */
export const GOOGLE_ADS_PKCE_VERIFIER_RE = /^[A-Za-z0-9._~-]{43,128}$/;
/** Kod sınaması (PKCE, S256): SHA-256'nın dolgusuz URL-güvenli base64'ü (43 karakter) */
export const GOOGLE_ADS_PKCE_CHALLENGE_RE = /^[A-Za-z0-9_-]{43}$/;
// ---------------------------------------------------------------------------------------------------------------------
// Hesap durumu, erişim seviyesi, kapalı durumlar
// ---------------------------------------------------------------------------------------------------------------------
/** Hesap durumu (sağlayıcının `CustomerStatus` değerinin kapalı küme karşılığı) */
export const GOOGLE_ADS_ACCOUNT_STATUSES = [
    'enabled',
    'canceled',
    'suspended',
    'closed',
    'unknown',
];
const STATUS_BY_NAME = {
    ENABLED: 'enabled',
    CANCELED: 'canceled',
    SUSPENDED: 'suspended',
    CLOSED: 'closed',
};
export function googleAdsAccountStatusOf(raw) {
    return raw === null || raw === undefined ? 'unknown' : (STATUS_BY_NAME[raw] ?? 'unknown');
}
/**
 * Bağlama akışına özgü ret nedenleri (`error.details.reason`). Sağlayıcının ham yanıtı taşınmaz.
 *   provider_not_available     gerçek kip bu süreçte kapalı ("onay bekliyor")
 *   simulator_disabled         simülatör bu ortamda kapalı
 *   state_invalid              `state` yok, kullanılmış, süresi dolmuş ya da başka kullanıcıya/oturuma ait
 *   code_rejected              sağlayıcı yetki kodunu kabul etmedi (kullanılmış / süresi dolmuş / doğrulayıcı uyuşmuyor)
 *   provider_rejected          sağlayıcı sonraki bir okumayı reddetti
 *   scope_missing              okuma için gereken kapsam verilmedi
 *   offline_access_missing     sağlayıcı kalıcı yetki (yenileme belirteci) vermedi — bağlantı kurulamaz
 *   not_ads_user               bu girişin eriştiği reklam hesabı yok
 *   two_step_required          hesabın güvenlik ayarı (iki adımlı doğrulama / geçiş anahtarı) tamamlanmamış
 *   admin_policy               kullanıcının kuruluş yöneticisi bu uygulamaya izin vermiyor
 *   platform_config            Castinflow'un sağlayıcı yapılandırması reddedildi (kullanıcı düzeltemez; destek)
 *   production_access_pending  seçilen hesap üretim hesabı; erişim seviyesi yalnız test hesaplarına izin veriyor
 *   account_inactive           seçilen hesap etkin değil (iptal / askıda / kapalı) ve test hesabı da değil
 *   ticket_invalid             seçim bileti yok, kullanılmış, süresi dolmuş ya da başka kullanıcıya/oturuma ait
 *   account_not_in_ticket      seçilen hesap bu biletin listesinde yok
 *   asset_already_bound        hesap başka bir çalışma alanına bağlı
 *   manager_account            seçilen kimlik bir YÖNETİCİ hesabı (harcama kaynağı olarak bağlanmaz; altındaki hesap seçilir)
 *   authorization_superseded   yetkilendirme başladıktan sonra seçilen hesabın bağlantısı değişti (kesildi / başka oturumda
 *                              yenilendi / bu arada bağlandı) — paylaşılan bağlama oturumunun dönem koruması
 *   provider_budget_exhausted  (v3.3) sağlayıcı okuma hakkı şu an dolu (platform bütçesi / çalışma alanı tavanı / sağlayıcının
 *                              kota yanıtı): hata değil BEKLEME — `details.retryAt` yer açılmasının beklendiği andır
 *   provider_budget_unconfigured (v3.3) bu ortamda okuma bütçesi tanımlı değil; gerçek çağrı yapılmaz (kullanıcı düzeltemez)
 */
export const GOOGLE_ADS_CONNECT_REASONS = [
    'provider_not_available',
    'simulator_disabled',
    'state_invalid',
    'code_rejected',
    'provider_rejected',
    'scope_missing',
    'offline_access_missing',
    'not_ads_user',
    'two_step_required',
    'admin_policy',
    'platform_config',
    'production_access_pending',
    'account_inactive',
    'ticket_invalid',
    'account_not_in_ticket',
    'asset_already_bound',
    'manager_account',
    'authorization_superseded',
    'provider_budget_exhausted',
    'provider_budget_unconfigured',
];
// ---------------------------------------------------------------------------------------------------------------------
// API sözleşmesi
// ---------------------------------------------------------------------------------------------------------------------
/**
 * Simülatör profili (yalnız simülatör kipinde; sahte sağlayıcının "bulut projesi" ve kullanıcı durumu):
 *   standard     üretim erişimi var: doğrudan hesap + yönetici ve alt hesapları + test hesabı
 *   test_access  erişim seviyesi test: üretim hesapları okunamaz, yalnız test hesabı bağlanabilir
 *   no_accounts  girişin eriştiği reklam hesabı yok
 */
export const GOOGLE_ADS_SIMULATOR_PROFILES = ['standard', 'test_access', 'no_accounts'];
/** `POST /settings/provider-connections/ads/google/authorize` */
export const googleAdsConnectAuthorizeInputSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('live') }).strict(),
    z
        .object({
        mode: z.literal('simulator'),
        /** Simülatörün sağlayıcı tarafı senaryosu (`expiring` = yetki 3 gün sonra doluyor) */
        scenario: z.enum(SIMULATOR_SCENARIOS).default('valid'),
        profile: z.enum(GOOGLE_ADS_SIMULATOR_PROFILES).default('standard'),
    })
        .strict(),
]);
/** Yanıt CA-4 ile aynıdır: gidilecek adres, dış adres mi, `state` bitişi */
export const googleAdsConnectAuthorizeResultSchema = adConnectAuthorizeResultSchema;
/** `POST /settings/provider-connections/ads/google/exchange` — `{ code, state }` (CA-4 ile aynı biçim) */
export const googleAdsConnectExchangeInputSchema = adConnectExchangeInputSchema;
/**
 * Hesabın SEÇİLEMEME nedeni (kapalı küme; `null` = seçilebilir):
 *   bound_elsewhere            başka bir çalışma alanına bağlı (hangisi olduğu söylenmez)
 *   production_access_pending  üretim hesabı; erişim seviyesi yalnız test hesaplarına izin veriyor
 *   inactive                   hesap etkin değil ve test hesabı da değil
 *   no_access                  sağlayıcı bu girişle hesabın okunmasına izin vermedi
 */
export const GOOGLE_ADS_ACCOUNT_BLOCKS = [
    'bound_elsewhere',
    'production_access_pending',
    'inactive',
    'no_access',
];
/** Yönetici hesabı — yalnız GRUP başlığı (harcama kaynağı olarak bağlanmaz, seçilemez) */
export const googleAdsConnectManagerSchema = z.object({
    /** Grup kimliği (bilet kapsamında opak; dış hesap kimliği DEĞİLDİR) */
    id: z.string().regex(AD_CONNECT_ACCOUNT_KEY_RE),
    /** Maskeli dış hesap kimliği (son 4 karakter) */
    externalIdMasked: z.string(),
    name: z.string().nullable(),
    testAccount: z.boolean(),
    /** Bu yöneticinin altındaki hesap listesi okuma sınırını aştı: yalnız ilk hesaplar listelendi */
    truncated: z.boolean(),
    /** Alt hesaplar okunamadı (sağlayıcı reddetti ya da yanıt vermedi): grup boş görünür */
    clientsUnavailable: z.boolean(),
});
export const googleAdsConnectAccountSchema = z.object({
    /** Seçim kimliği (bilet kapsamında opak; dış hesap kimliği DEĞİLDİR) */
    id: z.string().regex(AD_CONNECT_ACCOUNT_KEY_RE),
    externalIdMasked: z.string(),
    name: z.string().nullable(),
    /** ISO 4217 */
    currency: z.string().nullable(),
    /** IANA saat dilimi (hesabın rapor günü bu dilimdedir) */
    timeZone: z.string().nullable(),
    status: z.enum(GOOGLE_ADS_ACCOUNT_STATUSES),
    testAccount: z.boolean(),
    /** Erişim yolu: doğrudan ya da `managers` içindeki bir yönetici hesabı üzerinden */
    access: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('direct') }),
        z.object({
            kind: z.literal('manager'),
            managerId: z.string().regex(AD_CONNECT_ACCOUNT_KEY_RE),
        }),
    ]),
    blocked: z.enum(GOOGLE_ADS_ACCOUNT_BLOCKS).nullable(),
    /** Bu çalışma alanında zaten bağlıysa bağlantısı: seçilirse YETKİSİ YENİLENİR (varlık ve geçmiş korunur) */
    connection: z
        .object({
        id: z.string(),
        displayName: z.string(),
        status: z.enum(PROVIDER_CONNECTION_STATUSES),
    })
        .nullable(),
});
export const googleAdsConnectExchangeResultSchema = z.object({
    /** Seçim bileti (tek kullanımlık; belirteç bu değerde YOKTUR — sunucuda zarf şifreli tutulur) */
    ticket: z.string().regex(AD_CONNECT_OPAQUE_RE),
    /** Biletin geçerlilik bitişi (UTC, ISO 8601) */
    expiresAt: z.string(),
    simulated: z.boolean(),
    /** Bağlanabilecek reklam hesapları (yönetici hesapları burada YOKTUR) */
    accounts: z.array(googleAdsConnectAccountSchema),
    /** Yönetici hesapları — yalnız grup başlığı */
    managers: z.array(googleAdsConnectManagerSchema),
    /** Doğrudan erişilen hesap sayısı okuma sınırını aştı: liste eksik */
    truncated: z.boolean(),
    /** Bu süreçte bildirilen erişim seviyesi (hangi hesapların neden seçilemediğini açıklar) */
    accessLevel: z.enum(GOOGLE_ADS_ACCESS_LEVELS),
    /**
     * Sağlayıcının yetki için bildirdiği bitiş (UTC, ISO 8601); `null` = bitiş bildirilmedi. Doğrulanmamış uygulamada yetki
     * 7 günde dolar: kullanıcı bunu bağlamadan önce görür.
     */
    grantExpiresAt: z.string().nullable(),
    /** Reklam hesabı alt kotasının anlık durumu; karar `connect` anında sunucudadır */
    quota: planQuotaStatusSchema.optional(),
});
/** `POST /settings/provider-connections/ads/google/connect` — `{ ticket, accountIds, displayName? }` (CA-4 ile aynı biçim) */
export const googleAdsConnectConnectInputSchema = adConnectConnectInputSchema;
export const googleAdsConnectConnectResultSchema = adConnectConnectResultSchema;
// ---------------------------------------------------------------------------------------------------------------------
// Yardımcılar (yan etkisiz; istemci ve sunucu kullanır)
// ---------------------------------------------------------------------------------------------------------------------
/** Hesap seçilebilir mi (yalnız `blocked` boşsa) */
export function googleAdsAccountSelectable(a) {
    return a.blocked === null;
}
/** Hesap YENİ bağlantı mı açar (reklam hesabı kotasından yer harcar) — bağlı hesabın seçilmesi yetki yenilemedir */
export function googleAdsAccountIsNew(a) {
    return a.connection === null;
}
/**
 * Hesapları erişim yoluna göre gruplar: önce doğrudan erişilenler (varsa), sonra yanıt sırasıyla yönetici hesapları. Alt
 * hesabı olmayan yönetici de listelenir (boş grup dürüstçe gösterilir). Yöneticisi yanıtta bulunmayan hesap atılmaz, doğrudan
 * erişilenlerle birlikte gösterilir.
 */
export function groupGoogleAdsAccounts(x) {
    const known = new Set(x.managers.map((m) => m.id));
    const direct = x.accounts.filter((a) => a.access.kind === 'direct' || !known.has(a.access.managerId));
    const groups = [];
    if (direct.length > 0)
        groups.push({ manager: null, accounts: direct });
    for (const m of x.managers)
        groups.push({
            manager: m,
            accounts: x.accounts.filter((a) => a.access.kind === 'manager' && a.access.managerId === m.id),
        });
    return groups;
}
//# sourceMappingURL=google-ads-connect.js.map